import { useQuery } from '@tanstack/react-query'
import api from '../utils/api'

export interface XAIModel {
  id: string
  name: string
  description: string
  contextWindow: number
  inputPrice: number
  outputPrice: number
  capabilities: string[]
  isReasoning: boolean
  isVision: boolean
  aliases?: string[]
}

export interface ModelsResponse {
  models: XAIModel[]
  defaultModel: string
  lastUpdated: string
}

export interface RecommendationsResponse {
  general: { id: string; name: string }[]
  coding: { id: string; name: string }[]
  reasoning: { id: string; name: string }[]
  vision: { id: string; name: string }[]
  'long-context': { id: string; name: string }[]
}

export function useModels() {
  return useQuery<ModelsResponse>({
    queryKey: ['models'],
    queryFn: async () => {
      const response = await api.get('/models')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useModelRecommendations() {
  return useQuery<RecommendationsResponse>({
    queryKey: ['models', 'recommendations'],
    queryFn: async () => {
      const response = await api.get('/models/recommendations')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useModel(modelId: string) {
  return useQuery<{ model: XAIModel }>({
    queryKey: ['models', modelId],
    queryFn: async () => {
      const response = await api.get(`/models/${modelId}`)
      return response.data
    },
    enabled: !!modelId,
  })
}

// Format price for display
export function formatPrice(price: number): string {
  if (price === 0) return 'Free'
  if (price < 1) return `$${(price * 100).toFixed(0)}¢`
  return `$${price.toFixed(2)}`
}

// Get model display name
export function getModelDisplayName(model: XAIModel): string {
  return model.name
}

// Get model description with price
export function getModelDescription(model: XAIModel): string {
  const inputPrice = formatPrice(model.inputPrice)
  const outputPrice = formatPrice(model.outputPrice)
  return `${model.description} • ${inputPrice} in / ${outputPrice} out per 1M tokens`
}

// Filter models for swarm creation (only text-capable models)
export function getSwarmModels(models: XAIModel[]): XAIModel[] {
  return models.filter(m => 
    m.capabilities.includes('text') && 
    !m.id.includes('vision') // Exclude vision-only models for now
  )
}

// Get default model ID
export function getDefaultModelId(models: XAIModel[]): string {
  const defaultModel = models.find(m => m.id === 'grok-4-1-fast')
  return defaultModel?.id || models[0]?.id || 'grok-4-1-fast'
}
